/**
 * Upload Modal Component - Redesigned with clear visual hierarchy
 * Focus: PRIMARY action (upload) > Secondary info (tips) > Required (GDPR)
 */

import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { X, Upload, ArrowRight, Images, TrendingUp, Sparkles, CheckCircle } from 'lucide-react';

export default function UploadModal({ isOpen, onClose, onSubmit }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [filename, setFilename] = useState('');
  const [consentAuthorized, setConsentAuthorized] = useState(false);
  const [consentPropertyUse, setConsentPropertyUse] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFiles([]);
      setPreviews([]);
      setFilename('');
      setConsentAuthorized(false);
      setConsentPropertyUse(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview));
    };
  }, [previews]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const remainingSlots = 5 - files.length;
      if (remainingSlots <= 0) return;

      const filesToAdd = acceptedFiles.slice(0, remainingSlots);
      const newPreviews = filesToAdd.map(file => URL.createObjectURL(file));

      const updatedFiles = [...files, ...filesToAdd];
      const updatedPreviews = [...previews, ...newPreviews];

      setFiles(updatedFiles);
      setPreviews(updatedPreviews);

      if (updatedFiles.length === 1) {
        setFilename(updatedFiles[0].name);
      } else {
        setFilename(`${updatedFiles.length} Property Images`);
      }
    }
  }, [files, previews]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxSize: 10 * 1024 * 1024,
    maxFiles: 5,
    multiple: true
  });

  const handleSubmit = () => {
    if (files.length > 0 && consentAuthorized && consentPropertyUse) {
      onSubmit({
        files,
        filename,
        confirmed_authorized: consentAuthorized,
        confirmed_property_use: consentPropertyUse
      });
    }
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(previews[index]);
    const newFiles = files.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    setFiles(newFiles);
    setPreviews(newPreviews);

    if (newFiles.length === 1) {
      setFilename(newFiles[0].name);
    } else if (newFiles.length > 1) {
      setFilename(`${newFiles.length} Property Images`);
    } else {
      setFilename('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[#0a0a0a]/90 backdrop-blur-xl rounded-2xl w-full max-w-3xl max-h-[90vh] border border-white/10 shadow-2xl overflow-hidden flex flex-col">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary-500 to-transparent opacity-50" />
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative p-8 overflow-y-auto">
          {/* HIERARCHY LEVEL 1: Header (Context) */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">
                Property Analysis
              </h2>
              <p className="text-gray-400 text-sm">
                AI-powered location detection • Upload up to 5 images
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/5 text-gray-400 hover:text-white transition-all"
            >
              <X size={20} />
            </button>
          </div>

          {/* HIERARCHY LEVEL 2: PRIMARY ACTION (Upload Zone) */}
          {previews.length === 0 ? (
            <>
              <div
                {...getRootProps()}
                className={`
                  group relative border-2 border-dashed rounded-2xl px-8 py-10 text-center cursor-pointer
                  transition-all duration-300 mb-5
                  ${isDragActive
                    ? 'border-primary-500 bg-primary-500/10 shadow-[0_0_30px_rgba(0,209,133,0.2)] scale-[1.02]'
                    : 'border-white/20 bg-white/5 hover:border-primary-500/50 hover:bg-white/10 hover:shadow-xl'
                  }
                `}
              >
                <input {...getInputProps()} />

                <div className="flex flex-col items-center justify-center gap-4">
                  <div className={`p-4 rounded-2xl transition-all duration-300 ${isDragActive
                    ? 'bg-primary-500/20 text-primary-400 scale-110'
                    : 'bg-white/5 text-gray-400 group-hover:text-primary-400 group-hover:bg-primary-500/10 group-hover:scale-105'
                    }`}>
                    <Upload size={40} className={`transition-transform ${isDragActive ? 'animate-bounce' : ''}`} />
                  </div>

                  <div>
                    <p className="text-white font-semibold text-lg mb-1">
                      {isDragActive ? 'Drop your images here' : 'Drop images or click to browse'}
                    </p>
                    <p className="text-gray-500 text-xs">
                      JPG, PNG, or WEBP • Max 10MB each
                    </p>
                  </div>
                </div>
              </div>

              {/* HIERARCHY LEVEL 3: SECONDARY INFO (Helpful Tips) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Tip 1: Multi-image boost */}
                <div className="p-3 bg-gradient-to-br from-primary-500/10 to-blue-500/10 border border-primary-500/20 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary-500/20 rounded-lg shrink-0">
                      <TrendingUp size={20} className="text-primary-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold text-sm mb-1.5">
                        Multiple Images = Better Results
                      </h4>
                      <p className="text-gray-300 text-xs leading-relaxed">
                        3-5 exterior shots boost confidence from <span className="text-red-400 font-semibold">~12%</span> to <span className="text-primary-400 font-semibold">40-60%</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tip 2: Best practices */}
                <div className="p-3 bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg shrink-0">
                      <Sparkles size={20} className="text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold text-sm mb-1.5">
                        What Makes Good Photos?
                      </h4>
                      <p className="text-gray-300 text-xs leading-relaxed">
                        Clear building views • Different angles • Outdoor shots • Visible surroundings
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* HIERARCHY LEVEL 4: REQUIRED (GDPR - Collapsible preview) */}
              <div className="p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
                  <p className="text-xs font-semibold text-yellow-400 uppercase tracking-wider">
                    Required before upload
                  </p>
                </div>
                <p className="text-gray-400 text-xs">
                  You'll need to confirm authorization and property-use-only before analyzing
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-6">
              {/* Image Grid */}
              <div className="grid grid-cols-3 gap-3">
                {previews.map((preview, index) => (
                  <div key={index} className="relative rounded-xl overflow-hidden aspect-video group border border-white/10 shadow-lg bg-black/50 hover:border-primary-500/30 transition-all">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 p-1.5 bg-black/70 backdrop-blur-md border border-white/10 rounded-lg text-white/70 hover:text-white hover:bg-red-500/90 transition-all duration-200 opacity-0 group-hover:opacity-100"
                    >
                      <X size={14} />
                    </button>
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/70 backdrop-blur-md rounded border border-white/10 text-xs text-white font-medium">
                      #{index + 1}
                    </div>
                  </div>
                ))}

                {previews.length < 5 && (
                  <div
                    {...getRootProps()}
                    className="relative rounded-xl aspect-video border-2 border-dashed border-white/10 bg-white/5 hover:border-primary-500/50 hover:bg-white/10 cursor-pointer transition-all group flex items-center justify-center"
                  >
                    <input {...getInputProps()} />
                    <div className="text-center">
                      <Upload size={24} className="mx-auto text-gray-400 group-hover:text-primary-400 transition-colors mb-2" />
                      <p className="text-xs text-gray-400 group-hover:text-gray-300">Add More</p>
                      <p className="text-[10px] text-gray-600">{5 - previews.length} remaining</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Status Badge */}
              <div className="flex items-center justify-between p-3 bg-primary-500/10 border border-primary-500/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-primary-400" />
                  <span className="text-sm text-primary-400 font-medium">
                    {files.length} image{files.length > 1 ? 's' : ''} ready
                  </span>
                </div>
                {files.length >= 3 && (
                  <span className="px-2 py-1 bg-primary-500/20 text-primary-400 text-xs rounded-full font-medium">
                    ✨ Optimal range
                  </span>
                )}
              </div>

              {/* Reference Name */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Reference Name (Optional)
                </label>
                <input
                  type="text"
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/20 transition-all"
                  placeholder="e.g. Modern Villa in Lisbon"
                />
              </div>

              {/* GDPR Consents */}
              <div className="space-y-2 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
                  <p className="text-xs font-semibold text-yellow-400 uppercase tracking-wider">
                    Required Confirmations (GDPR)
                  </p>
                </div>

                <label className="flex items-start gap-3 cursor-pointer group p-2 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input
                      type="checkbox"
                      checked={consentAuthorized}
                      onChange={(e) => setConsentAuthorized(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${consentAuthorized
                      ? 'bg-primary-600 border-primary-600'
                      : 'border-white/30 bg-white/5 group-hover:border-primary-500/50'
                      }`}>
                      {consentAuthorized && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                    I have <strong className="text-white">authorization</strong> to analyze this property
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group p-2 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input
                      type="checkbox"
                      checked={consentPropertyUse}
                      onChange={(e) => setConsentPropertyUse(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${consentPropertyUse
                      ? 'bg-primary-600 border-primary-600'
                      : 'border-white/30 bg-white/5 group-hover:border-primary-500/50'
                      }`}>
                      {consentPropertyUse && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                    This is for <strong className="text-white">property identification only</strong>, not tracking people
                  </span>
                </label>

                <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-white/5">
                  🔒 Images deleted after analysis • Anonymized audit logs maintained
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
                <button
                  onClick={onClose}
                  className="px-6 py-3 text-gray-400 hover:text-white transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!consentAuthorized || !consentPropertyUse}
                  className={`
                    group flex items-center gap-2 px-8 py-3 rounded-xl font-semibold shadow-lg transition-all duration-300
                    ${!consentAuthorized || !consentPropertyUse
                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed opacity-50'
                      : 'bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white shadow-primary-500/20 hover:shadow-primary-500/40 hover:-translate-y-0.5'
                    }
                  `}
                >
                  {!consentAuthorized || !consentPropertyUse
                    ? 'Accept confirmations to continue'
                    : `Analyze ${files.length} Image${files.length > 1 ? 's' : ''}`}
                  <ArrowRight size={18} className={`transition-transform ${consentAuthorized && consentPropertyUse ? 'group-hover:translate-x-1' : ''}`} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
